import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import { RoleGuideBanner } from '../components/RoleGuideBanner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { toast } from 'sonner';
import {
  CreditCard, Shield, FileCheck, ChevronRight, ExternalLink,
  CheckCircle, AlertTriangle, TrendingUp, Home, Briefcase,
  Users, Search, Star, ArrowRight, Building2, Calculator, MapPin
} from 'lucide-react';
import ProfessionalResourcesSection from '../components/ProfessionalResourcesSection';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getAffordabilityColor = (ratio) => {
  if (ratio < 30) return 'text-emerald-600';
  if (ratio < 40) return 'text-amber-600';
  return 'text-red-600';
};

/* ─── Step Indicator ─── */
const StepIndicator = ({ steps, current }) => (
  <div className="flex items-center justify-center gap-2 mb-10 flex-wrap" data-testid="step-indicator">
    {steps.map((s, i) => (
      <React.Fragment key={s.label}>
        <button
          onClick={() => s.onClick?.()}
          className={`flex items-center gap-2 px-4 py-2 text-xs font-semibold tracking-wider uppercase transition-all ${
            i === current
              ? 'bg-[#E07A3A] text-white'
              : i < current
              ? 'bg-emerald-100 text-emerald-700'
              : 'bg-slate-100 text-slate-400'
          }`}
          data-testid={`step-${i}`}
        >
          {i < current ? <CheckCircle className="h-3.5 w-3.5" /> : <span>{i + 1}</span>}
          <span className="hidden sm:inline">{s.label}</span>
        </button>
        {i < steps.length - 1 && <ChevronRight className="h-4 w-4 text-slate-300" />}
      </React.Fragment>
    ))}
  </div>
);

/* ─── Step 1: Experian Credit Score Guide ─── */
const ExperianGuideStep = ({ guide, country, onNext }) => {
  if (!guide) return <div className="text-center py-20 text-slate-400">Loading guide...</div>;

  return (
    <div className="max-w-4xl mx-auto" data-testid="experian-guide-step">
      <div className="text-center mb-10">
        <Shield className="h-12 w-12 text-[#E07A3A] mx-auto mb-4" />
        <h2 className="font-['Playfair_Display'] text-3xl text-[#0F172A] font-semibold mb-2">Check Your Credit Score</h2>
        <p className="text-slate-500 text-sm max-w-xl mx-auto">Before applying for a mortgage, you need to know your credit score. Follow these steps to get your free score from {guide.provider}.</p>
      </div>

      {/* Steps */}
      <div className="space-y-4 mb-10">
        {guide.steps.map((step) => (
          <div key={step.step} className="flex gap-4 p-5 border border-slate-100 hover:border-[#E07A3A]/20 transition-colors" data-testid={`experian-step-${step.step}`}>
            <div className="w-10 h-10 flex-shrink-0 bg-[#FEF3EC] text-[#E07A3A] flex items-center justify-center font-bold text-sm">{step.step}</div>
            <div>
              <h4 className="text-[#0F172A] font-semibold text-sm mb-1">{step.title}</h4>
              <p className="text-slate-500 text-sm leading-relaxed">{step.description}</p>
              {step.url && (
                <a href={step.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#E07A3A] text-xs font-semibold mt-2 hover:underline">
                  Open {guide.provider} <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Score Ranges */}
      <div className="mb-10">
        <h3 className="font-semibold text-[#0F172A] text-sm tracking-wider uppercase mb-4">Score Ranges & Mortgage Approval Likelihood</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {Object.entries(guide.score_ranges).map(([key, range]) => {
            const colors = { excellent: 'border-emerald-200 bg-emerald-50', good: 'border-blue-200 bg-blue-50', fair: 'border-amber-200 bg-amber-50', poor: 'border-red-200 bg-red-50', very_poor: 'border-red-300 bg-red-100' };
            const textColors = { excellent: 'text-emerald-700', good: 'text-blue-700', fair: 'text-amber-700', poor: 'text-red-700', very_poor: 'text-red-800' };
            return (
              <div key={key} className={`p-4 border ${colors[key] || 'border-slate-200 bg-slate-50'}`} data-testid={`score-range-${key}`}>
                <div className="flex justify-between items-center mb-1">
                  <span className={`font-bold text-sm uppercase tracking-wider ${textColors[key]}`}>{key.replace('_', ' ')}</span>
                  <span className="text-xs text-slate-500">{range.min}-{range.max}</span>
                </div>
                <p className="text-xs text-slate-500 mb-1">Approval: <strong>{range.approval_likelihood}</strong></p>
                <p className="text-xs text-slate-400">{range.tip}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tips */}
      <div className="bg-[#0F172A] p-6 mb-10">
        <h3 className="text-white font-semibold text-sm tracking-wider uppercase mb-4">Tips to Improve Your Score</h3>
        <div className="grid sm:grid-cols-2 gap-2">
          {guide.tips.map((tip, i) => (
            <div key={`tip-${tip.slice(0, 20)}`} className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
              <span className="text-white/70 text-xs">{tip}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="text-center">
        <Button onClick={onNext} className="bg-[#E07A3A] text-white hover:bg-[#C4652E] px-10 py-6 text-sm tracking-widest uppercase font-semibold" data-testid="experian-next-btn">
          I Know My Score — Continue <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

/* ─── Step 2: Pre-Qualification Form ─── */
const PreQualifyStep = ({ country, onResult, loading, setLoading }) => {
  const { countryInfo } = useCountry();
  const [form, setForm] = useState({
    annual_income: '', monthly_expenses: '', deposit_amount: '', property_price: '',
    employment_type: 'employed', employment_years: '', credit_score_range: 'good',
    existing_debts: '', dependents: '0', first_time_buyer: true,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.annual_income || !form.deposit_amount) {
      toast.error('Please fill in at least your income and deposit amount');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        annual_income: parseFloat(form.annual_income),
        monthly_expenses: parseFloat(form.monthly_expenses) || 0,
        deposit_amount: parseFloat(form.deposit_amount),
        property_price: form.property_price ? parseFloat(form.property_price) : null,
        employment_type: form.employment_type,
        employment_years: form.employment_years ? parseInt(form.employment_years) : null,
        credit_score_range: form.credit_score_range,
        country,
        existing_debts: parseFloat(form.existing_debts) || 0,
        dependents: parseInt(form.dependents) || 0,
        first_time_buyer: form.first_time_buyer,
      };
      const res = await axios.post(`${API_URL}/api/mortgage/pre-qualify`, payload);
      onResult(res.data);
      toast.success('Pre-qualification summary generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate pre-qualification');
    } finally {
      setLoading(false);
    }
  };

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <div className="max-w-3xl mx-auto" data-testid="prequalify-form-step">
      <div className="text-center mb-10">
        <Calculator className="h-12 w-12 text-[#E07A3A] mx-auto mb-4" />
        <h2 className="font-['Playfair_Display'] text-3xl text-[#0F172A] font-semibold mb-2">Mortgage Pre-Qualification</h2>
        <p className="text-slate-500 text-sm max-w-xl mx-auto">Enter your financial details to get an AI-powered pre-qualification estimate. This does not affect your credit score.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs tracking-wider uppercase text-slate-500 font-semibold">Annual Income ({countryInfo.symbol}) *</Label>
            <Input type="number" value={form.annual_income} onChange={(e) => update('annual_income', e.target.value)} placeholder="e.g. 50000" required data-testid="input-income" />
          </div>
          <div>
            <Label className="text-xs tracking-wider uppercase text-slate-500 font-semibold">Monthly Expenses ({countryInfo.symbol})</Label>
            <Input type="number" value={form.monthly_expenses} onChange={(e) => update('monthly_expenses', e.target.value)} placeholder="e.g. 1500" data-testid="input-expenses" />
          </div>
          <div>
            <Label className="text-xs tracking-wider uppercase text-slate-500 font-semibold">Deposit Available ({countryInfo.symbol}) *</Label>
            <Input type="number" value={form.deposit_amount} onChange={(e) => update('deposit_amount', e.target.value)} placeholder="e.g. 25000" required data-testid="input-deposit" />
          </div>
          <div>
            <Label className="text-xs tracking-wider uppercase text-slate-500 font-semibold">Target Property Price ({countryInfo.symbol})</Label>
            <Input type="number" value={form.property_price} onChange={(e) => update('property_price', e.target.value)} placeholder="Optional" data-testid="input-property-price" />
          </div>
          <div>
            <Label className="text-xs tracking-wider uppercase text-slate-500 font-semibold">Existing Monthly Debts ({countryInfo.symbol})</Label>
            <Input type="number" value={form.existing_debts} onChange={(e) => update('existing_debts', e.target.value)} placeholder="e.g. 200" data-testid="input-debts" />
          </div>
          <div>
            <Label className="text-xs tracking-wider uppercase text-slate-500 font-semibold">Dependents</Label>
            <Input type="number" value={form.dependents} onChange={(e) => update('dependents', e.target.value)} placeholder="0" data-testid="input-dependents" />
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs tracking-wider uppercase text-slate-500 font-semibold">Employment Type</Label>
            <select value={form.employment_type} onChange={(e) => update('employment_type', e.target.value)} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm mt-1" data-testid="select-employment">
              <option value="employed">Employed</option>
              <option value="self_employed">Self-Employed</option>
              <option value="contractor">Contractor</option>
              <option value="retired">Retired</option>
            </select>
          </div>
          <div>
            <Label className="text-xs tracking-wider uppercase text-slate-500 font-semibold">Years in Employment</Label>
            <Input type="number" value={form.employment_years} onChange={(e) => update('employment_years', e.target.value)} placeholder="e.g. 5" data-testid="input-employment-years" />
          </div>
          <div>
            <Label className="text-xs tracking-wider uppercase text-slate-500 font-semibold">Credit Score Range</Label>
            <select value={form.credit_score_range} onChange={(e) => update('credit_score_range', e.target.value)} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm mt-1" data-testid="select-credit-score">
              <option value="excellent">Excellent</option>
              <option value="good">Good</option>
              <option value="fair">Fair</option>
              <option value="poor">Poor</option>
              <option value="unknown">I Don't Know</option>
            </select>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <input type="checkbox" checked={form.first_time_buyer} onChange={(e) => update('first_time_buyer', e.target.checked)} className="w-4 h-4 accent-[#E07A3A]" data-testid="checkbox-ftb" />
          <Label className="text-sm text-slate-600">I am a first-time buyer</Label>
        </div>

        <div className="text-center pt-4">
          <Button type="submit" disabled={loading} className="bg-[#E07A3A] text-white hover:bg-[#C4652E] px-10 py-6 text-sm tracking-widest uppercase font-semibold" data-testid="prequalify-submit-btn">
            {loading ? 'Generating AI Summary...' : 'Get Pre-Qualification Summary'}
            {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
          </Button>
          <p className="text-xs text-slate-400 mt-3">Powered by Gemini AI. Does not affect your credit score.</p>
        </div>
      </form>
    </div>
  );
};

/* ─── Step 3: Results ─── */
const ResultsStep = ({ result, onNext }) => {
  if (!result) return null;
  const pq = result.pre_qualification;
  const statusColors = { strong: 'text-emerald-600 bg-emerald-50 border-emerald-200', moderate: 'text-amber-600 bg-amber-50 border-amber-200', stretched: 'text-red-600 bg-red-50 border-red-200' };
  const statusLabels = { strong: 'Strong Position', moderate: 'Moderate — Manageable', stretched: 'Stretched — Caution Advised' };

  return (
    <div className="max-w-4xl mx-auto" data-testid="results-step">
      <div className="text-center mb-10">
        <TrendingUp className="h-12 w-12 text-[#E07A3A] mx-auto mb-4" />
        <h2 className="font-['Playfair_Display'] text-3xl text-[#0F172A] font-semibold mb-2">Your Pre-Qualification Summary</h2>
        <div className={`inline-flex items-center gap-2 px-4 py-2 border text-sm font-semibold tracking-wider uppercase ${statusColors[result.status]}`} data-testid="affordability-status">
          {result.status === 'strong' ? <CheckCircle className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
          {statusLabels[result.status]}
        </div>
      </div>

      {/* Key Numbers */}
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <div className="text-center p-6 border border-slate-100" data-testid="result-max-borrowing">
          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Max Borrowing</p>
          <p className="font-['Playfair_Display'] text-3xl text-[#E07A3A] font-semibold">{pq.symbol}{pq.max_borrowing.toLocaleString('en-GB')}</p>
          <p className="text-xs text-slate-400 mt-1">{pq.income_multiplier}x your income</p>
        </div>
        <div className="text-center p-6 border border-slate-100" data-testid="result-affordable-price">
          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Affordable Price</p>
          <p className="font-['Playfair_Display'] text-3xl text-[#0F172A] font-semibold">{pq.symbol}{pq.affordable_property_price.toLocaleString('en-GB')}</p>
          <p className="text-xs text-slate-400 mt-1">{pq.deposit_percent}% deposit</p>
        </div>
        <div className="text-center p-6 border border-slate-100" data-testid="result-monthly-payment">
          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Monthly Payment</p>
          <p className="font-['Playfair_Display'] text-3xl text-[#0F172A] font-semibold">{pq.symbol}{pq.estimated_monthly_payment.toLocaleString('en-GB')}</p>
          <p className="text-xs text-slate-400 mt-1">{pq.interest_rate_estimate}% over 25 years</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-8">
        <div className="p-4 border border-slate-100">
          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">LTV Ratio</p>
          <p className="text-lg font-semibold text-[#0F172A]">{pq.ltv_ratio}%</p>
        </div>
        <div className="p-4 border border-slate-100">
          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Affordability Ratio</p>
          <p className={`text-lg font-semibold ${getAffordabilityColor(pq.affordability_ratio)}`}>{pq.affordability_ratio}%</p>
        </div>
      </div>

      {/* AI Summary */}
      {result.ai_summary && (
        <div className="bg-[#0F172A] p-8 mb-8" data-testid="ai-summary">
          <div className="flex items-center gap-2 mb-4">
            <Star className="h-5 w-5 text-[#E07A3A]" />
            <h3 className="text-white font-semibold text-sm tracking-wider uppercase">AI Advisor Analysis</h3>
          </div>
          <div className="text-white/70 text-sm leading-relaxed whitespace-pre-wrap">{result.ai_summary}</div>
        </div>
      )}

      {/* Next Steps */}
      <div className="mb-8">
        <h3 className="font-semibold text-[#0F172A] text-sm tracking-wider uppercase mb-4">Recommended Next Steps</h3>
        <div className="space-y-3">
          {result.next_steps.map((step, i) => (
            <div key={`step-${step.slice(0, 20)}`} className="flex items-start gap-3 p-3 border border-slate-100">
              <div className="w-6 h-6 bg-[#FEF3EC] text-[#E07A3A] flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</div>
              <p className="text-sm text-slate-600">{step}</p>
            </div>
          ))}
        </div>
      </div>

      <p className="text-xs text-slate-400 text-center mb-8 italic">{result.disclaimer}</p>

      <div className="text-center">
        <Button onClick={onNext} className="bg-[#E07A3A] text-white hover:bg-[#C4652E] px-10 py-6 text-sm tracking-widest uppercase font-semibold" data-testid="results-next-btn">
          Find Professionals <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

/* ─── Step 4: Professional Directory ─── */
const ProfessionalDirectoryStep = ({ directory }) => {
  const { user } = useAuth();
  const { country } = useCountry();
  const [activeType, setActiveType] = useState(null);
  const [selectedPro, setSelectedPro] = useState(null);
  const [consultMessage, setConsultMessage] = useState('');
  const [sending, setSending] = useState(false);

  if (!directory) return <div className="text-center py-20 text-slate-400">Loading professionals...</div>;

  const categories = directory.categories || [];
  const professionals = directory.professionals || [];
  const filtered = activeType ? professionals.filter(p => p.professional_type === activeType) : professionals;

  const typeIcons = {
    mortgage_advisor: <CreditCard className="h-5 w-5" />,
    solicitor: <FileCheck className="h-5 w-5" />,
    surveyor: <Search className="h-5 w-5" />,
    estate_agent: <Building2 className="h-5 w-5" />,
    financial_advisor: <TrendingUp className="h-5 w-5" />,
  };

  const handleRequestConsultation = async (pro) => {
    if (!user) {
      toast.error('Please login to request a consultation');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/consultations/request`, {
        professional_id: pro.id,
        service_type: pro.professional_type || 'general',
        message: consultMessage,
        country,
      });
      toast.success(`Consultation request sent to ${pro.name}!`);
      setSelectedPro(null);
      setConsultMessage('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send request');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="professional-directory-step">
      <div className="text-center mb-10">
        <Users className="h-12 w-12 text-[#E07A3A] mx-auto mb-4" />
        <h2 className="font-['Playfair_Display'] text-3xl text-[#0F172A] font-semibold mb-2">Choose Your Professionals</h2>
        <p className="text-slate-500 text-sm max-w-xl mx-auto">Select the professionals you need for your property purchase. Request a free consultation — they'll guide you through mortgage, legal, and survey processes.</p>
      </div>

      {/* Category Filters */}
      <div className="flex flex-wrap justify-center gap-3 mb-8">
        <button onClick={() => setActiveType(null)} className={`px-4 py-2 text-xs font-semibold tracking-wider uppercase transition-all ${!activeType ? 'bg-[#E07A3A] text-white' : 'border border-slate-200 text-slate-500 hover:border-[#E07A3A] hover:text-[#E07A3A]'}`} data-testid="filter-all">
          All
        </button>
        {categories.map((cat) => (
          <button key={cat.type} onClick={() => setActiveType(cat.type)} className={`flex items-center gap-1.5 px-4 py-2 text-xs font-semibold tracking-wider uppercase transition-all ${activeType === cat.type ? 'bg-[#E07A3A] text-white' : 'border border-slate-200 text-slate-500 hover:border-[#E07A3A] hover:text-[#E07A3A]'}`} data-testid={`filter-${cat.type}`}>
            {typeIcons[cat.type]} {cat.label}
          </button>
        ))}
      </div>

      {/* Professional Cards */}
      {filtered.length > 0 ? (
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          {filtered.map((pro) => (
            <div key={pro.id} className="p-5 border border-slate-100 hover:border-[#E07A3A]/30 hover:shadow-md transition-all" data-testid={`pro-card-${pro.id}`}>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#0F172A] flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-semibold text-sm">{(pro.name || '?').split(' ').map(n => n[0]).join('').slice(0, 2)}</span>
                </div>
                <div className="flex-1">
                  <h4 className="text-[#0F172A] font-semibold text-sm">{pro.name}</h4>
                  <p className="text-xs text-[#E07A3A] font-semibold uppercase tracking-wider">{(pro.professional_type || 'general').replace(/_/g, ' ')}</p>
                  {pro.location && <p className="text-xs text-slate-400 mt-1"><MapPin className="h-3 w-3 inline mr-1" />{pro.location}</p>}
                  {pro.bio && <p className="text-xs text-slate-500 mt-2 leading-relaxed line-clamp-2">{pro.bio}</p>}
                  {pro.service_areas && pro.service_areas.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {pro.service_areas.map(area => (
                        <span key={area} className="px-2 py-0.5 bg-slate-100 text-slate-500 text-[10px] uppercase tracking-wider font-medium">{area}</span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-3 mt-3">
                    <span className="text-xs text-slate-400"><Briefcase className="h-3 w-3 inline mr-1" />{pro.completed_deals || 0} deals</span>
                    {pro.years_experience && <span className="text-xs text-slate-400">{pro.years_experience}+ years</span>}
                  </div>
                </div>
              </div>

              {/* Request Consultation Button */}
              {selectedPro?.id === pro.id ? (
                <div className="mt-4 pt-4 border-t border-slate-100" data-testid={`consult-form-${pro.id}`}>
                  <textarea
                    value={consultMessage}
                    onChange={(e) => setConsultMessage(e.target.value)}
                    placeholder="Introduce yourself and describe what you need help with..."
                    className="w-full border border-slate-200 p-3 text-sm focus:border-[#E07A3A] focus:outline-none resize-none h-20"
                    data-testid={`consult-message-${pro.id}`}
                  />
                  <div className="flex gap-2 mt-2">
                    <Button
                      onClick={() => handleRequestConsultation(pro)}
                      disabled={sending}
                      className="flex-1 bg-[#E07A3A] text-white hover:bg-[#C4652E] text-xs tracking-wider"
                      data-testid={`send-consult-${pro.id}`}
                    >
                      {sending ? 'Sending...' : 'Send Request'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => { setSelectedPro(null); setConsultMessage(''); }}
                      className="text-xs tracking-wider border-slate-200"
                    >
                      Cancel
                    </Button>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-2">Free consultation. Your pre-qualification data will be shared automatically.</p>
                </div>
              ) : (
                <Button
                  onClick={() => setSelectedPro(pro)}
                  className="w-full mt-4 bg-[#0F172A] text-white hover:bg-[#1e293b] text-xs tracking-widest uppercase font-semibold py-5"
                  data-testid={`request-consult-btn-${pro.id}`}
                >
                  Request Free Consultation <ArrowRight className="ml-2 h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 border border-slate-100 mb-8">
          <Users className="h-12 w-12 text-slate-200 mx-auto mb-4" />
          <h3 className="text-lg text-[#0F172A] font-semibold mb-2">No Professionals Yet</h3>
          <p className="text-sm text-slate-400 max-w-md mx-auto">We're actively onboarding mortgage advisors, solicitors, and surveyors. Register as a professional to be listed here.</p>
        </div>
      )}

      {/* Professional directory deep-links */}
      <div className="mb-6">
        <ProfessionalResourcesSection context="mortgage" country={country || 'UK'} />
      </div>

      {/* CTA for Professionals */}
      <div className="bg-[#0F172A] p-8 text-center" data-testid="pro-join-cta">
        <Briefcase className="h-10 w-10 text-[#E07A3A] mx-auto mb-4" />
        <h3 className="font-['Playfair_Display'] text-2xl text-white font-semibold mb-2">Are You a Mortgage Advisor, Solicitor, or Surveyor?</h3>
        <p className="text-white/50 text-sm max-w-lg mx-auto mb-6">Join Shopperden's professional directory and receive pre-verified client leads. Access warm leads from buyers who are ready to transact — no cold calls needed.</p>
        <div className="flex flex-wrap justify-center gap-4">
          {['Pre-verified buyer leads', 'Free consultation requests', 'Compliance-ready data', 'Growing marketplace'].map((benefit) => (
            <span key={benefit} className="flex items-center gap-1.5 text-white/70 text-xs"><CheckCircle className="h-3 w-3 text-emerald-400" />{benefit}</span>
          ))}
        </div>
        <a href="/register">
          <Button className="mt-6 bg-[#E07A3A] text-white hover:bg-[#C4652E] px-8 py-5 text-sm tracking-widest uppercase font-semibold" data-testid="pro-register-btn">
            Register as Professional <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </a>
      </div>
    </div>
  );
};

/* ─── Main Page ─── */
const MortgageGuidePage = () => {
  const { user } = useAuth();
  const { country } = useCountry();
  const [step, setStep] = useState(0);
  const [guide, setGuide] = useState(null);
  const [result, setResult] = useState(null);
  const [directory, setDirectory] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchGuide = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/mortgage/experian-guide?country=${country}`);
      setGuide(res.data);
    } catch (err) { logger.error('Failed to load experian guide:', err); }
  }, [country]);

  const fetchDirectory = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/professionals/directory`);
      setDirectory(res.data);
    } catch (err) { logger.error('Failed to load directory:', err); }
  }, []);

  useEffect(() => {
    fetchGuide();
    fetchDirectory();
  }, [country, fetchGuide, fetchDirectory]);

  const steps = [
    { label: 'Credit Score', onClick: () => setStep(0) },
    { label: 'Pre-Qualify', onClick: () => user ? setStep(1) : toast.error('Please login to use the pre-qualification tool') },
    { label: 'Results', onClick: () => result ? setStep(2) : null },
    { label: 'Professionals', onClick: () => setStep(3) },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      {user && <RoleGuideBanner currentSection="/buyer" />}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Page Header */}
        <div className="text-center mb-8">
          <span className="text-xs tracking-[0.3em] text-[#E07A3A] mb-2 block uppercase font-semibold">Mortgage Journey</span>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl text-[#0F172A] font-semibold">Your Path to<span className="italic text-[#E07A3A]"> Home Ownership</span></h1>
          <p className="text-slate-500 mt-3 max-w-xl mx-auto text-sm">From credit check to completion — we guide you through every step of getting your mortgage approved.</p>
        </div>

        <StepIndicator steps={steps} current={step} />

        {step === 0 && <ExperianGuideStep guide={guide} country={country} onNext={() => user ? setStep(1) : toast.error('Please login to continue')} />}
        {step === 1 && <PreQualifyStep country={country} onResult={(r) => { setResult(r); setStep(2); }} loading={loading} setLoading={setLoading} />}
        {step === 2 && <ResultsStep result={result} onNext={() => setStep(3)} />}
        {step === 3 && <ProfessionalDirectoryStep directory={directory} />}
      </div>
    </div>
  );
};

export default MortgageGuidePage;
